import { SummaryType } from './ai-summary/ai-chat-box.service';
import { CoreUtils } from './core-utils';

export class BWErrorRS {
  errorCategory: string;
  errorCode: string;
  statusCode: number;
  errorMessages: string[];

  constructor(category: string, code: string, status: number, messages: string[]) {
    this.errorCategory = category;
    this.errorCode = code;
    this.statusCode = status;
    this.errorMessages = messages;
  }

  public static isBWError(response: any): boolean {
    if (
      response &&
      response.result &&
      response.result.errorCategory &&
      response.result.errorCode &&
      response.result.statusCode &&
      response.result.errorMessages &&
      response.result.errorMessages.length > 0
    ) {
      return true;
    }
    return false;
  }

  public static fromObjects(objects?: any[]): BWErrorRS {
    let ret: BWErrorRS;

    if (objects && objects.length > 0) {
      for (let i = 0; i < objects.length; i++) {
        let obj = objects[i];

        if (obj) {
          if (obj instanceof BWErrorRS) {
            return obj as BWErrorRS;
          } else if (BWErrorRS.isBWError(obj)) {
            return BWErrorRS.from(obj) as BWErrorRS;
          } else if (obj.constructor.name.toLowerCase() === 'string') {
            const tmp = CoreUtils.parseJsonSafe(obj as string);

            if (BWErrorRS.isBWError(tmp)) {
              return BWErrorRS.from(tmp) as BWErrorRS;
            }
          }
        }
      }

      ret = BWErrorRS.from(objects[0]) as BWErrorRS;
    } else {
      ret = BWErrorRS.createDefault();
    }

    return ret;
  }

  public static from(response: any, createFallback: boolean = false): BWErrorRS | null {
    if (BWErrorRS.isBWError(response)) {
      const obj = response.result;
      return new BWErrorRS(obj.errorCategory, obj.errorCode, obj.statusCode, obj.errorMessages);
    }

    if (createFallback) {
      const msg = response ? [JSON.stringify(response)] : null;
      return BWErrorRS.createDefault(msg);
    }

    return null;
  }

  public static fromArrayBuffer(data: ArrayBuffer, createFallback: boolean = false): BWErrorRS | null {
    const strErr = CoreUtils.arrayBufferToText(data);

    if (strErr) {
      try {
        const obj = JSON.parse(strErr);
        const ret = BWErrorRS.from(obj, false);

        if (ret) return ret;
        else if (!createFallback) return null;
      } catch {}

      return BWErrorRS.createDefault([strErr]);
    }

    if (createFallback) return BWErrorRS.createDefault(null);

    return null;
  }

  public getErrorMessage(separator: string = ' '): string {
    let ret = 'Unknown Error';

    if (!separator) {
      separator = ' ';
    }

    if (this.errorMessages && this.errorMessages.length > 0) {
      var errmsg = JSON.parse(this.errorMessages[0]);
      ret = errmsg.result.errorMessages.join(separator);
    }

    return ret;
  }

  public static createDefault(messages?: string[] | null): BWErrorRS {
    if (!messages || messages.length == 0) messages = ['Unknown Error'];
    return new BWErrorRS('System', 'BW102000', 500, messages);
  }
}

export interface DownloadDocumentRQ {
  type: number;
  resourceIds: string[];
  documentIds?: string[];
  params?: string[];
}

export interface DownloadedArrayBufferRS {
  filename: string | null;
  mimeType: string | null;
  data: ArrayBuffer | null;
}

export interface DocSummaryRQ {
  resourceId: string;
  resourceType: string;
  summaryType: SummaryType;
  chatId?: number;
  userQuestion?: string | null;
}

export interface StaticPrompt {
  id: number;
  title: string;
  userRole: string;
  active: boolean;
  platform: string;
  question: string;
  context: string;
  rankOrder: number;
  type: string;
  createdAt: string;
  updatedAt: string;
}

export interface Citation {
  '@search.score': number;
  '@search.rerankerScore': number;
  id: string;
  title: string;
  url: string;
  reference_url: string;
  description: string | null;
  persona: string;
  keywords: string[] | null;
  video_urls: string[] | null;
}

export type StatusAPIRS = QuestionStatusRS | DoneResponse;

export interface QuestionStatusRS {
  kind: 'statusResponse';
  status: Status;
}

export interface Status {
  value: string;
  conversationId: number;
}

export interface CreateChatRS {
  chatId: number;
  version: string;
}

export interface ConversationResponseChunk {
  data: string;
}
export interface HistoryConversationMetaData {
  reference_Urls: string;
  citations: string;
  navigation_Links: Record<string, string>;
  searchResultFields: string;
  entityResults: object;
  assistantMessage: string;
}

export interface StaticPromptError {
  name: string | null;
  message: string | null;
}

export interface StaticPromptResponse {
  statusCode: number;
  error: StaticPromptError;
  message: string;
  data: StaticPrompt[];
  version: string;
}

export interface StaticPromptMiniWidgetResponse {
  result: string[];
}

export interface CardItem {
  id: string | number;
  title: string;
  description: string;
  question?: string;
  icon?: string;
}

export interface HistoryConversationRS {
  statusCode: number;
  error: StaticPromptError;
  chat_Id: number;
  data: HistoryMessage[];
  version: string;
}

export type ChatMessage = HistoryMessage | LiveMessage;

export interface LiveMessage {
  id?: number;
  kind: 'live';
  userPrompt: string;
  metadata?: ConversationData[];
  chatId?: number;
}

export interface HistoryMessage {
  id: number;
  categoryId: number;
  userPrompt: string;
  isError: boolean;
  statusId: number | null;
  category: string;
  createdAt: string;
  userSatisfaction: number | null;
  userSatisfactionNote: string | null;
  metadata: HistoryConversationMetaData;
  kind: 'history';
  isSaveSummaryDisabled?: boolean;
}

export enum ShortcutCardType {
  ChatSupport = 'CHAT_SUPPORT'
}

export type AssistantResponse = AssistantResponseChunk | ConversationCompletionResponse | DoneResponse | StreamingFailedResponse;

export interface DoneResponse {
  kind: 'doneMessage';
  message: string;
}

export interface AssistantResponseChunk {
  kind: 'assistantMessage';
  AssistantMessage: string;
}

export interface ConversationCompletionResponse {
  kind: 'completionResponse';
  statusCode: number;
  error: StaticPromptError;
  data: ConversationData[];
  screenView: string;
  screenName: string | null;
  debugResponses: Record<string, unknown>;
}
export interface ConversationData {
  chatId: number;
  completionId: number;
  category: string;
  userQuestion: string;
  assistantMessage: string;
  questionId: number | null;
  citations: Citation[];
  navigation_links: Record<string, string> | null;
}

export interface StreamingFailedResponse {
  kind: 'streamingFailed';
  message: string;
  correlationId: string;
}

export type ErrorCode = Record<
  number,
  {
    message: string;
    rerun: boolean;
  }
>;

export type ActivePanel = SupportBotPanelInputs | SummaryBotPanelInputs | SavedSummaryPanelInputs | null;

export interface SupportBotPanelInputs {
  kind: 'supportBotPanel';
  initialPrompt: string;
  startNewChat: boolean;
}

export interface SummaryBotPanelInputs {
  kind: 'summaryBotPanel';
  isOneDriveDoc: boolean;
  documentName: string;
  resourceId: string;
  resourceType: string;
}

export interface SavedSummaryPanelInputs {
  kind: 'savedSummaryPanel';
  isOneDriveDoc: boolean;
  documentName: string;
  resourceId: string;
  resourceType: string;
}

export interface ExpandSummaryParams {
  text: string;
  label?: string;
  displaySaveSummary: boolean;
  chatId?: number;
  conversationId?: number;
  isSummarySaved?: boolean;
  summaryTitle: string;
}

export interface SaveSummaryPopUpParams {
  chatId: number | null;
  conversationId: number | null;
  alreadyHasSummary: boolean | null;
}

export interface SaveSummaryCompletePopUpParams {
  previousAccessType: string | null;
  previousSummaryName: string | null;
  isSummaryChanged: boolean;
}

export interface SaveSummaryParams {
  chatId: number | null;
  conversationId: number | null;
  accessType: string;
  summaryContent?: string;
  resourceId?: string;
  resourceType?: string;
  replacePrevious?: boolean;
  userQuestion?: string;
  isConfirmedReplace?: boolean;
}

export interface ReplaceSummaryState {
  chatId: number;
  conversationId: number;
}

export interface SaveSummaryResponse {
  correlationId: string;
  result: boolean;
}

export interface SavedSummaryResponse {
  result: SummaryItem[];
  correlationId: string;
}

export interface SummaryItem {
  summaryId: string;
  accessType: 'Private' | 'Shared';
  createdBy: string;
  createdByName: string;
  createdDate: string;
  modifiedDate: string;
  canDelete: boolean;
  summaryPreview: string;
  userProfile?: Profile;
  userQuestion?: string;
}

export interface Profile {
  userId: string;
  name: string;
  isDisabled: boolean;
  isDeleted: boolean;
}

export interface ProfileResponse {
  result: Profile[];
  correlationId: string;
}

export interface SaveSummaryResponse {
  correlationId: string;
  result: boolean;
}

export interface DeleteSummaryParams {
  summaryId: string;
}

export interface DeleteSummaryResponse {
  result: boolean;
  correlationId: string;
}

export interface ConversationHistoryRS {
  statusCode: number;
  error?: { name: string; message: string };
  chat_Id?: number;
  data: ConversationHistoryItem[];
  version: string;
  isDocumentUpdated?: boolean;
}

export interface ConversationHistoryItem {
  id: number;
  categoryId: number;
  userPrompt: string;
  isError: boolean;
  statusId?: number;
  category: string;
  createdAt: string;
  userSatisfaction?: number;
  userSatisfactionNote?: string;
  isSaveSummaryDisabled: boolean;
  metadata: {
    reference_Urls: string;
    citations: string;
    navigation_Links: { [key: string]: string };
    searchResultFields: string;
    entityResults: any;
    assistantMessage: string;
  };
}

export interface ViewSavedSummaryStateParams {
  activeTab: 'private' | 'shared';
  sharedBy: 'Shared by: You' | 'Shared by: Everyone';
  displaySharedByFilter: boolean;
  miniExpandedSummaryId: string | null;
  isSummaryOpened: boolean;
  fullSummaryDetails: string;
}

export interface AppUrls {
  ios?: string;
  android?: string;
  windows?: string;
}
export interface AllAppUrls {
  playStore: string;
  iphoneAppStore: string;
  ipadAppStore: string;
  microSoftStore: string;
  iosFallBackUrls: IosFallbackUrls;
}

export interface AppUrlsResponse {
  result: AllAppUrls;
  correlationId: string;
}

export interface IosFallbackUrls {
  iphoneFallbackUrl: string;
  ipadFallbackUrl: string;
}

